import { useEffect } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, MailCheck, PhoneOff, ListX, Globe2, ScrollText, KeyRound, Bot } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame } from "./_shared";

const GROUPS = [
  { icon: KeyRound, t: "Consent & permission", items: ["Consent source & timestamp", "Channel-specific permission", "Do-not-call / do-not-contact", "Unsubscribe processing", "Global & campaign suppression lists"] },
  { icon: MailCheck, t: "Deliverability & sending health", items: ["Sending-domain health", "SPF, DKIM & DMARC status", "Email verification", "Bounce & complaint monitoring", "Outreach frequency limits"] },
  { icon: Globe2, t: "Privacy & jurisdiction", items: ["GDPR", "CCPA / CPRA", "India DPDP support", "Calling-hour restrictions by location", "Call-recording disclosure rules"] },
  { icon: ScrollText, t: "Governance & audit", items: ["AI-generated-content disclosure", "Role-based access", "Isolated client workspaces", "Complete action & approval logs", "Human approval before risky actions"] },
];

function TrustMock() {
  const checks = [
    { t: "SPF", ok: true }, { t: "DKIM", ok: true }, { t: "DMARC", ok: true },
    { t: "Suppression list synced", ok: true }, { t: "Consent on file", ok: true }, { t: "Calling hours OK", ok: true },
  ];
  return (
    <BrowserFrame url="app.websitetech.pro/trust/health">
      <div className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-3">Pre-send compliance check</div>
      <div className="grid grid-cols-2 gap-2">
        {checks.map((c) => (
          <div key={c.t} className="flex items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-[12px] font-semibold text-emerald-700">
            <ShieldCheck size={14} /> {c.t}
          </div>
        ))}
      </div>
      <div className="mt-3 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-[11px] text-slate-500 flex items-center gap-2"><Bot size={13} /> AI-assistant identity disclosed to recipients</div>
    </BrowserFrame>
  );
}

export default function TrustCenter() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Trust & Compliance Center | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-trust-page">
      <RevHero eyebrow="AI Revenue Center™ · Trust & Compliance"
        title="Compliance isn't an add-on —" accent="it's the foundation."
        sub="Consent, suppression, deliverability, jurisdiction rules and full audit logs are built into every channel. Because outreach that ignores the rules costs you your domain, your reputation and your customers' trust."
        visual={<TrustMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="Built-in, not bolted-on" title="What the Trust Center enforces" sub="Every one of these runs automatically before a message goes out — and is logged after." />
        <div className="grid sm:grid-cols-2 gap-5 mt-8">
          {GROUPS.map((g, i) => (
            <motion.div key={g.t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-2xl border border-slate-200 bg-white card-shadow p-6">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-brand/10 grid place-items-center text-brand"><g.icon size={18} /></div>
                <h3 className="font-heading font-bold text-slate-900">{g.t}</h3>
              </div>
              <ul className="mt-4 space-y-2">
                {g.items.map((x) => <li key={x} className="flex items-start gap-2 text-sm text-slate-600"><ShieldCheck size={14} className="text-brand mt-0.5 shrink-0" /> {x}</li>)}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="Enterprise trust" title="Ready for teams and agencies" />
          <div className="grid sm:grid-cols-3 gap-4 mt-8">
            {[{ icon: KeyRound, t: "Role-based access & SSO" }, { icon: ListX, t: "Isolated client workspaces" }, { icon: PhoneOff, t: "Do-not-contact enforcement" }].map((x) => (
              <div key={x.t} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-xl bg-white text-brand grid place-items-center card-shadow"><x.icon size={18} /></div>
                <span className="font-semibold text-slate-800 text-sm">{x.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RevCTA title="Grow responsibly — and sleep at night." sub="Start with a free audit. We'll review your sending-domain health and consent setup at no cost." />
    </RevShell>
  );
}
